import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useCallback, useMemo } from "react";
import {
  hasSubscription,
  isBusinessSubscription,
  isProSubscription,
} from "@markit/common.utils";
import { PlanTypeV4, SubscriptionPlanType } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import { useNavigate } from "../../hooks/useNavigate";
import { NavigationId } from "../../navigation/AppParamList";
import { notificationActions } from "../../redux/slices/notificationSlice";
import { onCreatorPanelNavigatePath } from "../../utils/navigationUtils";

type MarkitPlusActionButtonProps = {
  mainAction?: boolean;
  small?: boolean;
  planType?: SubscriptionPlanType; // specify plan type to upgrade to
};

const MarkitPlusActionButton = (props: MarkitPlusActionButtonProps) => {
  const { mainAction, small, planType } = props;
  const { accountData } = useSelector(getAccountState).account;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const styles = {
    title: {
      fontSize: small ? 12 : 14,
      fontWeight: 500,
      color: mainAction ? Colors.WHITE : Colors.BLACK,
    },

    upgradeTitle: {
      fontSize: small ? 12 : 14,
      fontWeight: 500,
      color: Colors.BLACK,
    },

    headerRightButton: {
      paddingInline: 20,
      paddingBlock: 14,
      borderRadius: 100,
      whiteSpace: "nowrap",
    },

    smallHeaderRightButton: {
      paddingInline: 10,
      paddingBlock: 5,
      borderRadius: 100,
      whiteSpace: "nowrap",
      border: `0.5px solid ${Colors.GRAY2}`,
    },
  };

  const buttonTitle = useMemo(
    () => (
      <span style={styles.title}>
        {hasSubscription(accountData) ? "Markit" : "Get Markit"}
        <span style={{ color: Colors.BLUE5 }}>+</span>
      </span>
    ),
    [accountData, styles.title]
  );

  const showUpgradeButton = useMemo(
    () =>
      hasSubscription(accountData) &&
      !isBusinessSubscription(accountData) &&
      mainAction,
    [accountData, mainAction]
  );

  const showMainButton = useMemo(
    () => !hasSubscription(accountData) || mainAction,
    [accountData, mainAction]
  );

  const onPress = useCallback(() => {
    if (hasSubscription(accountData)) {
      onCreatorPanelNavigatePath(navigate, NavigationId.HOME_SETTINGS, 1);
    } else {
      dispatch(
        notificationActions.setMarkitPlusModal(
          planType ? planType.name : PlanTypeV4.ESSENTIAL.name
        )
      );
    }
  }, [accountData, dispatch, navigate, planType]);

  const onUpgradePress = useCallback(() => {
    dispatch(
      notificationActions.setMarkitPlusModal(
        isProSubscription(accountData)
          ? PlanTypeV4.BUSINESS.name
          : PlanTypeV4.PRO.name
      )
    );
  }, [accountData, dispatch]);

  return showMainButton || showUpgradeButton ? (
    <>
      <div className="AlignedRow" style={{ gap: 10 }}>
        {small ? (
          <div
            className="AlignedRowSelect"
            onClick={onPress}
            style={{
              ...styles.smallHeaderRightButton,
              backgroundColor:
                hasSubscription(accountData) || mainAction
                  ? Colors.WHITE
                  : Colors.WHITE1,
            }}
          >
            {buttonTitle}
          </div>
        ) : null}
        {!small && showUpgradeButton ? (
          <div
            className="AlignedRowSelect"
            onClick={onUpgradePress}
            style={{
              ...styles.headerRightButton,
              border: `0.5px solid ${Colors.GRAY2}`,
              backgroundColor: Colors.WHITE1,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <span style={styles.upgradeTitle}>Upgrade</span>
          </div>
        ) : null}
        {!small && showMainButton ? (
          <div
            className="AlignedRowSelect"
            onClick={onPress}
            style={{
              ...styles.headerRightButton,
              border: `0.5px solid ${mainAction ? Colors.BLUE5 : Colors.GRAY2}`,
              backgroundColor: mainAction
                ? Colors.BLACK
                : hasSubscription(accountData)
                  ? Colors.WHITE
                  : Colors.WHITE1,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            {buttonTitle}
          </div>
        ) : null}
      </div>
    </>
  ) : null;
};

export default MarkitPlusActionButton;
